import { Check, UserPlus } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { TaskCardProps } from '@/types';

type AssigneeOption = NonNullable<TaskCardProps['assigneeOptions']>[number];
type Assignee = NonNullable<TaskCardProps['assignees']>[number];

interface TaskAssigneePickerProps {
  taskId: number;
  taskUuid?: string;
  assignees?: Assignee[];
  assigneeOptions?: AssigneeOption[];
  selectedAssigneeIds?: string[];
  fallbackAssigneeName?: string;
  align?: 'start' | 'center' | 'end';
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
  onAssigneeToggle?: TaskCardProps['onAssigneeToggle'];
}

export function TaskAssigneePicker({
  taskId,
  taskUuid,
  assignees = [],
  assigneeOptions = [],
  selectedAssigneeIds = [],
  fallbackAssigneeName,
  align = 'end',
  size = 'sm',
  disabled = false,
  className,
  onAssigneeToggle,
}: TaskAssigneePickerProps) {
  const preview = assignees.length
    ? assignees
    : assigneeOptions.filter((user) => selectedAssigneeIds.includes(user.id));
  const visibleCount = size === 'md' ? 5 : 3;
  const visiblePreview = preview.slice(0, visibleCount);
  const hiddenCount = Math.max(0, preview.length - visibleCount);
  const avatarClass = size === 'md' ? 'h-7 w-7 text-[10px]' : 'h-7 w-7 text-[10px]';
  const addClass = size === 'md' ? 'h-8 px-3 gap-2 text-xs' : 'h-7 w-7 p-0';

  const stopCardEvent = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {visiblePreview.length > 0 ? (
          <button
            type="button"
            draggable={false}
            disabled={disabled}
            className={cn(
              'flex items-center -space-x-2 rounded-full outline-none transition-transform hover:scale-105 disabled:pointer-events-none disabled:opacity-60',
              className
            )}
            aria-label="Open assignee selector"
            onClick={stopCardEvent}
            onPointerDown={stopCardEvent}
            onMouseDown={stopCardEvent}
            onDragStart={(event) => event.preventDefault()}
          >
            {visiblePreview.map((user) => (
              <Tooltip key={user.id}>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Avatar className={cn('border border-background ring-1 ring-border/20', avatarClass)}>
                      <AvatarImage src={user.photo_url || undefined} />
                      <AvatarFallback className="bg-primary/70 font-bold text-primary">
                        {user.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{user.name}</TooltipContent>
              </Tooltip>
            ))}
            {hiddenCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Avatar className={cn('border border-background ring-1 ring-border/20', avatarClass)}>
                      <AvatarFallback className="bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-200 font-bold">
                        +{hiddenCount}
                      </AvatarFallback>
                    </Avatar>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{hiddenCount} more assignee{hiddenCount > 1 ? 's' : ''}</TooltipContent>
              </Tooltip>
            )}
          </button>
        ) : fallbackAssigneeName ? (
          <button
            type="button"
            draggable={false}
            disabled={disabled}
            className={cn('rounded-full outline-none transition-transform hover:scale-105 disabled:pointer-events-none disabled:opacity-60', className)}
            aria-label="Open assignee selector"
            onClick={stopCardEvent}
            onPointerDown={stopCardEvent}
            onMouseDown={stopCardEvent}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={cn('rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center text-primary font-bold border border-primary/20 shadow-sm', avatarClass)}>
                  {fallbackAssigneeName.charAt(0).toUpperCase()}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{fallbackAssigneeName}</TooltipContent>
            </Tooltip>
          </button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            disabled={disabled}
            className={cn(
              'rounded-full border border-dashed border-primary/30 bg-primary/5 text-primary hover:bg-primary/10',
              addClass,
              className
            )}
            aria-label="Add assignee"
            onClick={stopCardEvent}
            onPointerDown={stopCardEvent}
            onMouseDown={stopCardEvent}
          >
            <UserPlus className={size === 'md' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            {size === 'md' && <span>Add assignee</span>}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={align}
        side="bottom"
        sideOffset={10}
        className="w-[min(92vw,320px)] p-0 overflow-hidden rounded-3xl border-border/60 bg-background shadow-2xl z-30"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border/60 px-3 py-2">
          <div className="text-xs font-semibold">Assign users</div>
          <div className="text-[10px] text-muted-foreground">
            Click a user to toggle assignment
          </div>
        </div>
        <Command>
          <CommandInput placeholder="Search users..." className="h-10" />
          <CommandList className="max-h-[280px]">
            <CommandEmpty>No users found.</CommandEmpty>
            <CommandGroup>
              {assigneeOptions.map((user) => {
                const selected = selectedAssigneeIds.includes(user.id);
                return (
                  <CommandItem
                    key={user.id}
                    value={`${user.name} ${user.schema_name} ${user.email || ''}`}
                    onSelect={() => {
                      onAssigneeToggle?.({
                        taskId,
                        taskUuid,
                        assignee: user,
                        selected,
                      });
                    }}
                    className="flex items-center gap-3"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.photo_url || undefined} />
                      <AvatarFallback className="bg-primary/70 text-white text-[10px] font-bold">
                        {user.name?.slice(0, 1)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{user.name}</p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {user.schema_name}
                      </p>
                    </div>
                    {selected ? (
                      <div className="h-4 w-4 rounded-full border border-emerald-500 bg-emerald-500/10 flex items-center justify-center">
                        <Check className="h-3 w-3 text-emerald-500" />
                      </div>
                    ) : (
                      <div className="h-4 w-4 rounded-full border border-border/60" />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
